import { CityDropdownForTrip, CommonInput } from 'components';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaCheck, FaCrown } from 'react-icons/fa6';
import { RiInformationLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
import toast from 'react-hot-toast';

import coupon from '../../assets/img/coupon.svg';
import shield from '../../assets/img/shield.svg';
import logo from "../../assets/img/transport360Logo.svg";
import { ConfirmModalFor, PaymentBillingModal } from 'modals';
import { UserContext } from 'context';
import { StorageServices } from 'utils';
import { applyDiscountCodeApi, createOrderPayment, getCompanyDetailsApi } from '_services';
import { FaArrowLeftLong } from "react-icons/fa6";
import './subscription.css'
import { getCityLocationList } from '_services/accountsServices/cityLocationService';
import StorageService from 'utils/StorageServices';
// import { CASHFREE_MODE } from 'lib/app-config';

const GST_RATE_DIVISOR = 100;

const initialFormData = {
  company_id: StorageServices.getCompanyId(),
  order_id: '',
  total_years: 1,
  total_amount: 0,
  plan_price: 0,
  gst_price: 0,
  coupon: '',
  selected_subscription_id: '',
  customised_plan: {
    branches: 0,
    users: 0,
  },
  gst_details: {
    gst_number: '',
    company_name: '',
    email_address: '',
  },
  billing_address: {
    address: '',
    city: '',
    state: '',
    pin_code: '',
  },
};

const SubscriptionPlan = () => {
  const navigate = useNavigate();

  const CASHFREE_MODE = process.env.REACT_APP_CASHFREE_MODE;
  const TRANSPORT_MODE = process.env.REACT_APP_TRANSPORT_MODE;
  const API_URL = process.env.REACT_APP_API_URL;
  const STAGING_API_URL = process.env.REACT_APP_STAGING_API_URL;
  const LIVE_API_URL = process.env.REACT_APP_LIVE_API_URL;

  const { subscriptionDetails, setSubscriptionDetails } = useContext(UserContext);

  const [formData, setFormData] = useState(initialFormData);
  const [branches, setBranches] = useState(subscriptionDetails?.no_of_branch);
  const [users, setUsers] = useState(subscriptionDetails.min_no_of_user);
  const [hasGST, setHasGST] = useState(true);
  const [isOpenBillingModal, setIsOpenBillingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [companyDetailsLoaded, setCompanyDetailsLoaded] = useState(false); // Add this state
  const [discountAmount, setDiscountAmount] = useState(0)
  const isUserLimitReached = useMemo(() =>
    subscriptionDetails?.max_no_of_user === users,
    [subscriptionDetails?.max_no_of_user, users]
  );

  const [errors, setErrors] = useState({
    gst_details: {
      gst_number: "",
      company_name: "",
      email_address: "",
    },
    billing_address: {
      address: "",
      city: "",
      state: "",
      pin_code: "",
    },
  });

  const getComapnyDetailsHandle = async () => {
    try {
      const res = await getCompanyDetailsApi({ company_id: StorageService.getCompanyId() });
      const companyDetails = res?.data?.company;
      const formattedData = {
        ...formData,
        company_id: StorageServices.getCompanyId(),
        gst_details: {
          gst_number: companyDetails?.gst_no || '',
          company_name: companyDetails?.company || '',
          email_address: companyDetails?.email_id || '',
        },
        billing_address: {
          address: `${companyDetails?.address_line1 || ''} ${companyDetails?.address_line2 || ''}`.trim(),
          city: companyDetails?.city?.value || '',
          state: companyDetails?.state?.value || '',
          pin_code: companyDetails?.pincode || '',
        },
      };

      setFormData(formattedData);
      setCompanyDetailsLoaded(true); // Mark company details as loaded
    } catch (error) {
      console.error("Error fetching company details:", error);
      setCompanyDetailsLoaded(true); // Mark as loaded even on error to proceed with calculation
    }
  };

  const fetchFromCity = async (search, page, limit) => {
    const res = await getCityLocationList({
      countryCode: "IN",
      stateCode: "",
      offset: page,
      limit,
      search,
    });
    if (res?.responseCode === 200) {
      const list = res?.data?.all_cities.map((c) => ({ ...c, _id: c.name, label: `${c.name}` }))
      return {
        items: list,
        total: res?.data?.total_record,
      };
    } else {
      toast.error(res?.responseMessage);
    }
  };

  const generateOrderId = useCallback(() => {
    const now = new Date();
    const dateStr = now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0');
    const timeStr = String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    return `order_${dateStr}_${timeStr}`;
  }, []);

  const calculateTotal = useCallback((subscriptionDetails, branches, users, gstRate, discountAmount) => {
    if (!subscriptionDetails) {
      return { plan_price: 0, gst_price: 0, total_amount: 0 };
    }

    const { actual_rate, branch_rate, user_rate, no_of_branch, min_no_of_user, adjusted_price } = subscriptionDetails;

    const additionalBranches = Math.max(0, branches - no_of_branch);
    const additionalUsers = Math.max(0, users - min_no_of_user);

    // Step 1: Base plan price
    const planPrice = actual_rate + (branch_rate * additionalBranches) + (user_rate * additionalUsers);

    // Step 2: Pehle discount lagao
    const discountedPlanPrice = Math.max(0, planPrice - discountAmount);

    // Step 3: Phir adjustment
    const adjustPrice = discountedPlanPrice + (adjusted_price || 0);

    // Step 4: GST adjusted price pe
    const gstPrice = adjustPrice * gstRate;

    // Step 5: Total
    const totalAmount = adjustPrice + gstPrice;

    return {
      plan_price: Number(adjustPrice.toFixed(2)),
      gst_price: Number(gstPrice.toFixed(2)),
      total_amount: Number(totalAmount.toFixed(2)),
    };
  }, []);


  const formatIndianNumber = useCallback((num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  }, []);

  const validateField = (section, field, value) => {
    let error = "";
    const strValue = value?.toString() || "";

    if (section === "gst_details") {
      if (field === "company_name" && !strValue.trim()) {
        error = "Company name is required";
      }

      if (field === "email_address" && strValue.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(strValue)) {
          error = "Invalid email address";
        }
      }

      if (field === "gst_number" && hasGST) {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!strValue.trim()) {
          error = "GST number is required";
        } else if (!gstRegex.test(strValue.toUpperCase())) {
          error = "Invalid GST number";
        }
      }
    }

    if (section === "billing_address") {
      if (field === "address" && !strValue.trim()) {
        error = "Address is required";
      }
      if (field === "city" && !strValue.trim()) {
        error = "City is required";
      }
      if (field === "state" && !strValue.trim()) {
        error = "State is required";
      }
      if (field === "pin_code") {
        if (!strValue.trim()) {
          error = "Pin code is required";
        } else if (!/^\d{6}$/.test(strValue)) {
          error = "Invalid pin code";
        }
      }
    }

    return error;
  };

  const handleChange = (section, field, value) => {
    const parsedValue =
      typeof value === "string" && !isNaN(value) && value !== ""
        ? Number(value)
        : value;

    setFormData((prev) => {
      if (!section) {
        return {
          ...prev,
          [field]: parsedValue,
        };
      }

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: parsedValue,
        },
      };
    });

    const error = validateField(section, field, parsedValue);
    setErrors((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: error || "",
      },
    }));
  };

  const validateGstDetails = () => {
    let isValid = true;

    const newGstErrors = {
      gst_number: "",
      company_name: "",
      email_address: "",
    };

    const { gst_number, company_name, email_address } = formData.gst_details;

    if (!company_name.trim()) {
      newGstErrors.company_name = "Company name is required";
      isValid = false;
    }

    if (email_address.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email_address)) {
        newGstErrors.email_address = "Invalid email format";
        isValid = false;
      }
    }

    if (hasGST) {
      if (!gst_number.trim()) {
        newGstErrors.gst_number = "GST number is required";
        isValid = false;
      } else {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(gst_number.toUpperCase())) {
          newGstErrors.gst_number = "Invalid GST number format";
          isValid = false;
        }
      }
    }

    setErrors((prev) => ({
      ...prev,
      gst_details: newGstErrors,
    }));

    return isValid;
  };

  const validateBillingDetails = () => {
    let isValid = true;

    const newBillingErrors = {
      address: "",
      city: "",
      state: "",
      pin_code: "",
    };

    const { address, city, state, pin_code } = formData.billing_address;

    if (!address.trim()) {
      newBillingErrors.address = "Address is required";
      isValid = false;
    }
    if (!city.trim()) {
      newBillingErrors.city = "City is required";
      isValid = false;
    }
    if (!state.trim()) {
      newBillingErrors.state = "State is required";
      isValid = false;
    }

    const pin = pin_code?.toString() || "";
    if (!pin.trim()) {
      newBillingErrors.pin_code = "Pin code is required";
      isValid = false;
    } else if (!/^\d{6}$/.test(pin)) {
      newBillingErrors.pin_code = "Invalid pin code";
      isValid = false;
    }

    setErrors((prev) => ({
      ...prev,
      billing_address: newBillingErrors,
    }));

    return isValid;
  };

  const clearBillingAddress = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      billing_address: {
        address: '',
        city: '',
        state: '',
        pin_code: '',
      },
    }));
    const newErrors = {
      ...errors,
      billing_address: {
        address: "",
        city: "",
        state: "",
        pin_code: "",
      },
    };

    setErrors(newErrors);
  }, []);

  const handleBranchChange = useCallback((increment) => {
    const newBranches = Math.max(0, branches + increment);
    setBranches(newBranches);
    handleChange('customised_plan', 'branches', newBranches);
  }, [branches, handleChange]);

  const handleUserChange = useCallback((increment) => {
    if (increment > 0 && isUserLimitReached) {
      toast.error(`You can add maximum ${subscriptionDetails?.max_no_of_user} users in this plan`);
      return;
    }
    const newUsers = Math.max(0, users + increment);
    setUsers(newUsers);
    handleChange('customised_plan', 'users', newUsers);
  }, [users, isUserLimitReached, subscriptionDetails?.max_no_of_user, handleChange]);

  const initializeCashfreePayment = useCallback(async (paymentSessionId) => {
    try {
      const cashfree = await load({ mode: CASHFREE_MODE });

      const checkoutOptions = {
        paymentSessionId,
        redirectTarget: "_self",
        appearance: {
          width: "500px",
          height: "650px",
          primaryColor: "#3B82F6",
          backgroundColor: "#FFFFFF",
          theme: {
            primaryColor: "#3B82F6",
            secondaryColor: "#F3F4F6",
            backgroundColor: "#FFFFFF",
            textColor: "#1F2937",
            borderColor: "#E5E7EB",
            errorColor: "#EF4444",
            successColor: "#10B981"
          }
        },
        modal: {
          closeOnOverlayClick: false,
          closeOnEsc: false
        },
        merchant: {
          name: "Transport 360",
          logo: logo,
        }
      };

      const result = await cashfree.checkout(checkoutOptions);
    } catch (error) {
      console.error("Cashfree SDK error:", error);
      toast.error("Payment system error. Please try again.");
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);


    // ✅ Dynamic return URL based on current environment
    const getReturnUrl = () => {
      const currentUrl = window.location.origin; // Gets current domain
      return `${currentUrl}/dashboard`;
    };

    const payload = {
      ...formData,
      discount_price: discountAmount || 0,
      selected_subscription_id: subscriptionDetails?._id,
      order_id: generateOrderId(),
      // return_url:
      //   CASHFREE_MODE === "sandbox"
      //     ? (TRANSPORT_MODE === "staging" ? STAGING_API_URL : API_URL) + "/dashboard"
      //     : (TRANSPORT_MODE === "staging" ? STAGING_API_URL : LIVE_API_URL) + "/dashboard",

      return_url: getReturnUrl(), // ✅ Use dynamic URL
      company_id: StorageServices.getCompanyId()
    };

    try {
      const res = await createOrderPayment(payload);

      if (res?.responseCode === 200) {
        const { order_id, payment_session_id } = res.data;

        if (!payment_session_id) {
          toast.error("Failed to create payment session");
          return;
        }

        clearBillingAddress();
        setIsOpenBillingModal(false);

        await initializeCashfreePayment(payment_session_id);
      } else {
        toast.error(res?.message || "Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [formData, subscriptionDetails?._id, generateOrderId, clearBillingAddress, initializeCashfreePayment]);

  // Load company details on mount
  useEffect(() => {
    getComapnyDetailsHandle();
  }, []);

  // Calculate totals when subscription details, branches, users change OR when company details are loaded
  useEffect(() => {
    // Only calculate if we have subscription details AND company details have been loaded
    if (!subscriptionDetails?._id || !companyDetailsLoaded) {
      return;
    }

    const gstRate = (subscriptionDetails?.gst_perc || 0) / GST_RATE_DIVISOR;
    const { plan_price, gst_price, total_amount } = calculateTotal(
      subscriptionDetails,
      branches,
      users,
      gstRate,
      discountAmount
    );

    setFormData((prev) => ({
      ...prev,
      plan_price,
      gst_price,
      total_amount,
    }));
  }, [subscriptionDetails, branches, users, calculateTotal, companyDetailsLoaded, discountAmount]); // Add companyDetailsLoaded to dependencies

  // Navigate away if no subscription details
  useEffect(() => {
    if (!subscriptionDetails?._id && companyDetailsLoaded) {
      navigate("/subscription");
      return;
    }
  }, [subscriptionDetails?._id, navigate, companyDetailsLoaded]);

  const handleGstSubmit = (e) => {
    e.preventDefault();
    if (validateGstDetails()) {
      setIsOpenBillingModal(true);
    }
  };

  const handleBillingSubmit = (e) => {
    e.preventDefault();
    if (validateBillingDetails()) {
      handleSubmit(e);
    }
  };

  const handleSubmitAll = (e) => {
    e.preventDefault();
    const isGstValid = validateGstDetails();
    const isBillingValid = validateBillingDetails();

    if (isGstValid && isBillingValid) {
      handleSubmit(e);
    } else {
    }
  };

  const handleDiscountCode = async () => {
    const payload = {
      company_id: StorageServices?.getCompanyId(),
      plan_id: subscriptionDetails?._id,
      coupon_code: formData.coupon,
      total_amount: formData.total_amount
    }

    try {
      const res = await applyDiscountCodeApi(payload)
      if (res?.responseCode == 200) {
        setDiscountAmount(res?.data?.amount)
      }
      console.log(res)
    } catch (error) {

    }
  }

  const payBillModalProps = useMemo(() => ({
    isOpen: isOpenBillingModal,
    closeModal: () => {
      clearBillingAddress();
      setIsOpenBillingModal(false);
    },
    modalMainClass: "_dashCommon_Modal __payBil_modal",
    setFormData,
    formData,
    handleChange,
    handleSubmit: handleBillingSubmit,
    loading,
    errors
  }), [isOpenBillingModal, clearBillingAddress, formData, handleChange, handleSubmit, loading]);

  if (!subscriptionDetails?._id && companyDetailsLoaded) {
    return null;
  }

  let confirmModalProps = {
    show: showMessage,
    closeModal: (e) => setShowMessage(false),
    title: "Message",
    confirmModal: "confirmModal",
  };

  return (
    <>
      <button className="back-btn" onClick={() => navigate("/subscription")}>
        <FaArrowLeftLong className="back-icon" />
        Back To Plan
      </button>
      <div className='__selPlan_mainBx'>
        <div className='__selPlan_container'>
          <div className='__selPlan_firstBx'>
            <div className="accordion" id="planAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button d-flex align-items-center"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#selectedPlan"
                    aria-expanded="true"
                    aria-controls="selectedPlan"
                  >
                    <span className="__selPlan_checked"><FaCheck /></span>
                    <span className='__selPlan_acordHead'>Selected Plan</span>
                    <span className="__selPlan_badge">
                      <span><FaCrown /></span>
                      {subscriptionDetails.plan_name}
                    </span>
                  </button>
                </h2>
                <div id="selectedPlan" className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPlan__details">
                      <div>
                        <h5 className="__selPlan__deHead">{subscriptionDetails.plan_name}</h5>
                        <div className='__selPlBadge_bx'>
                          <span className="__selPlan__deBadge">1 year</span>
                          <span className="__selPlan__deBadge">
                            {`${subscriptionDetails.min_no_of_user} Users`}
                          </span>
                          <span className="__selPlan__deBadge">
                            {`${subscriptionDetails.no_of_branch} Branch`}
                          </span>
                        </div>
                      </div>
                      <div className="__selPlan__deAmt text-end">
                        <h5 className="__selPlan__dePrc">
                          {`₹ ${Number(subscriptionDetails?.actual_rate || 0)}`}
                        </h5>
                        <small className="__selPlan__deAnnl">
                          {`@ ₹${subscriptionDetails?.actual_rate || 0} / billed annually`}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#customisePlan"
                    aria-expanded="true"
                    aria-controls="customisePlan"
                  >
                    <span className='__selPlan_acordHead'>Customise Plan</span>
                  </button>
                </h2>
                <div id="customisePlan" className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPlan_customBx">
                      <div className='__delPlan_numTx'>
                        <p>Number of Branch</p>
                        <small>{`₹${subscriptionDetails?.branch_rate?.toFixed(2) || '0.00'} / Branch`}</small>
                      </div>
                      <div className="input-group">
                        <button
                          className="btn"
                          onClick={() => handleBranchChange(-1)}
                          disabled={branches === subscriptionDetails?.no_of_branch}
                          style={{
                            backgroundColor: branches === subscriptionDetails?.no_of_branch ? '#d3d3d3' : '',
                            cursor: branches === subscriptionDetails?.no_of_branch ? 'not-allowed' : 'pointer'
                          }}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          className="form-control text-center"
                          value={`${branches} Branch${branches !== 1 ? "es" : ""}`}
                          readOnly
                        />
                        <button
                          className="btn"
                          onClick={() => handleBranchChange(1)}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="__selPlan_customBx">
                      <div className='__delPlan_numTx'>
                        <p>Number of User</p>
                        <small>{`₹${subscriptionDetails?.user_rate?.toFixed(2) || '0.00'} / User`}</small>
                      </div>
                      <div className="input-group">
                        <button
                          className="btn"
                          onClick={() => handleUserChange(-1)}
                          disabled={users === subscriptionDetails.min_no_of_user}
                          style={{
                            backgroundColor: users === subscriptionDetails.min_no_of_user ? '#d3d3d3' : '',
                            cursor: users === subscriptionDetails.min_no_of_user ? 'not-allowed' : 'pointer'
                          }}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          className="form-control text-center"
                          value={`${users} User${users !== 1 ? "s" : ""}`}
                          readOnly
                        />
                        <button
                          className="btn"
                          onClick={() => {
                            if (isUserLimitReached) {
                              setShowMessage(true)
                            } else {
                              handleUserChange(1)
                            }
                          }}
                          // disabled={isUserLimitReached}
                          style={{
                            backgroundColor: users === subscriptionDetails?.max_no_of_user ? '#d3d3d3' : '',
                            cursor: users === subscriptionDetails?.max_no_of_user ? 'not-allowed' : 'pointer'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item __notAccordion">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="billDetails"
                  >
                    <span className='__selPlan_acordHead'>Billing Details</span>
                  </button>
                </h2>
                <div className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPl_bilGst">
                      <span>I have GSTIN:</span>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={hasGST}
                          onChange={(e) => setHasGST(e.target.checked)}
                        />
                      </div>
                    </div>

                    <div className='__selInput_flx'>
                      {hasGST && (
                        <div className="__billInput_bx">
                          <CommonInput
                            placeholder="e.g., 27AABCCDDEEFFG"
                            label="Enter GST number"
                            value={formData.gst_details.gst_number}
                            onChange={(e) => handleChange('gst_details', 'gst_number', e.target.value)}
                            error={errors.gst_details.gst_number}
                          />
                        </div>
                      )}
                      <div className="__billInput_bx">
                        <CommonInput
                          placeholder="Enter company name"
                          label="Enter company name"
                          value={formData.gst_details.company_name}
                          onChange={(e) => handleChange('gst_details', 'company_name', e.target.value)}
                          error={errors.gst_details.company_name}
                        />
                      </div>
                      {/* ${hasGST ? "w-100" : ""} */}
                      {/* style={{ width: '100%' }} */}
                      <div className={`__billInput_bx  `}>
                        <CommonInput
                          placeholder="contact@company.com"
                          label="Enter email address"
                          type="email"
                          value={formData.gst_details.email_address}
                          onChange={(e) => handleChange('gst_details', 'email_address', e.target.value)}
                          error={errors.gst_details.email_address}
                        />
                      </div>
                      <div className={`__billInput_bx ${hasGST ? "w-100" : ""}`} >
                        <CommonInput label="Street address*" placeholder="Enter street address" value={formData.billing_address.address}
                          onChange={(e) => handleChange('billing_address', 'address', e.target.value)}
                          error={errors?.billing_address.address}

                        />
                      </div>
                      <div className="__billInput_bx">
                        <CityDropdownForTrip
                          key={"city"}
                          label="City"

                          placeholder="Select a city"
                          fetchData={fetchFromCity}
                          pageSize={20}
                          onChange={(data) => {
                            handleChange('billing_address', 'city', data.name)
                            handleChange("billing_address", "state", data.stateName)
                          }}

                          selectedItem={{
                            label: formData.billing_address.city
                              ? `${formData.billing_address.city}`
                              : "",
                            name: formData.billing_address.city
                              ? `${formData.billing_address.city}`
                              : "",
                            _id: `${formData.billing_address.city}`,
                          }}

                          withState={false}
                          error={errors?.billing_address.city}
                          errorMessage={errors?.billing_address.city}
                        />
                      </div>
                      <div className="__billInput_bx">

                        <CommonInput label="State*" value={formData.billing_address.state}
                          // onChange={(e) => handleChange('billing_address', 'pin_code', e.target.value)}
                          disabled={formData.billing_address.state}
                          placeholder="Enter State"
                          error={errors?.billing_address.state}
                        />
                      </div>

                      <div className={`__billInput_bx ${hasGST ? "w-100" : ""}`} >
                        <CommonInput label="Pin Code*" value={formData.billing_address.pin_code}
                          onChange={(e) => handleChange('billing_address', 'pin_code', e.target.value)}
                          placeholder="e.g., 560001"
                          error={errors?.billing_address.pin_code}
                          maxLength={6}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='__selPlan_secondBx'>
            <div className='__selPl_divider'></div>
          </div>

          <div className='__selPlan_thirdBx'>
            <div className="__selConfirm_planBx">
              <h5 className="__revHead mb-3">Review & Confirm Your Plan</h5>

              <div className='__flxr_seLplx'>
                <div className="__sled_pln_bxs">
                  <div className='__scr_prx'>
                    <div className="__sle_priceFlx_head">
                      <span>Plan Price (1 Year)</span>
                      <span>{`₹${formatIndianNumber(subscriptionDetails.actual_rate)}`}</span>
                    </div>

                    {/* Additional Branch */}
                    {branches > subscriptionDetails.no_of_branch && (
                      <div className="__sle_priceFlx">
                        <span>
                          {`${branches - subscriptionDetails.no_of_branch} Additional Branch${(branches - subscriptionDetails.no_of_branch) > 1 ? 'es' : ''}`}
                        </span>
                        <span>{`₹${formatIndianNumber(subscriptionDetails.branch_rate * (branches - subscriptionDetails.no_of_branch))}`}</span>
                      </div>
                    )}

                    {/* Additional Users */}
                    {users > subscriptionDetails.min_no_of_user && (
                      <div className="__sle_priceFlx">
                        <span>
                          {`${users - subscriptionDetails.min_no_of_user} Additional User${(users - subscriptionDetails.min_no_of_user) > 1 ? 's' : ''}`}
                        </span>
                        <span>{`₹${formatIndianNumber(subscriptionDetails.user_rate * (users - subscriptionDetails.min_no_of_user))}`}</span>
                      </div>
                    )}

                    {/* ✅ 1. Pehle Discount */}
                    {discountAmount > 0 && (
                      <div className="__sle_priceFlx" style={{ color: '#22C55E' }}>
                        <span>Discount (Coupon Applied)</span>
                        <span>{`- ₹${formatIndianNumber(discountAmount)}`}</span>
                      </div>
                    )}

                    {/* ✅ 2. Phir Adjustment */}
                    <div className="__sle_priceFlx">
                      <span>Current Plan Adjustment</span>
                      <span>{`₹${formatIndianNumber(subscriptionDetails?.adjusted_price)}`}</span>
                    </div>

                  </div>

                  {/* GST — adjusted price pe */}
                  <div className="__sle_gstFlx">
                    <span className='d-flex align-items-center gap-1.5'>
                      <span className='__sel_InfoBx'><RiInformationLine /></span>
                      {`GST ${subscriptionDetails.gst_perc}% of ₹${formatIndianNumber(formData.plan_price)}`}
                    </span>
                    <p>{`₹${formData.gst_price}`}</p>
                  </div>

                  <div className="__sel_totalFlx">
                    <h6 className="mb-0">Amount to Pay</h6>
                    <h5 className="text-primary mb-0">
                      {`₹${formatIndianNumber(formData.total_amount)}`}
                    </h5>
                  </div>


                  {/* <div className="__sle_gstFlx">
                    <span className='d-flex align-items-center gap-1.5'>
                      <span className='__sel_InfoBx'><RiInformationLine /></span>
                      {`GST ${subscriptionDetails.gst_perc}% of ₹${formatIndianNumber(formData.plan_price)}`}
                    </span>
                    <p>{`₹${formData.gst_price}`}</p>
                  </div>

                  <div className="__sel_totalFlx">
                    <h6 className="mb-0">Amount to Pay</h6>
                    <h5 className="text-primary mb-0">
                      {`₹${formatIndianNumber(formData.total_amount)}`}
                    </h5>
                  </div> */}
                </div>

                <div className="__refralBx">
                  <label className="form-label __revHead">
                    Apply Referral code or Discount code
                  </label>

                  <div className="input-group">
                    <div className="__inputRef">
                      <img src={coupon} alt="coupon" />

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter code"
                        value={formData.coupon}
                        onChange={(e) =>
                          handleChange("", "coupon", e.target.value.toUpperCase())
                        }
                      />

                      {formData.coupon && (
                        <span
                          className="__clearInput"
                          onClick={() => {
                            setDiscountAmount(0)
                            handleChange("", "coupon", "")
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </div>

                    <button className="__refApply_btn" onClick={handleDiscountCode}>
                      Apply
                    </button>
                  </div>
                </div>


                <div className='__selPay_btn'>
                  <button
                    className="anime-btn __payShield_btn"
                    onClick={handleSubmitAll}
                    disabled={loading}
                  >
                    <img src={shield} alt="shield" />
                    {loading ? "Proccessing..." : `Pay Securely ₹${formatIndianNumber(formData.total_amount)}`}
                  </button>
                  <p>
                    {`Exclusive of all taxes (${subscriptionDetails.gst_perc}% GST)`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {isOpenBillingModal && (
        <PaymentBillingModal {...payBillModalProps} />
      )}

      {showMessage && (
        <ConfirmModalFor {...confirmModalProps}>
          <>
            <p className="_confirmPrx">
              {" "}
              Your current plan has reached the maximum user limit. Please upgrade your plan to add more users.

            </p>
          </>
        </ConfirmModalFor>
      )}
    </>
  );
};

export default SubscriptionPlan;